import { createFileRoute } from '@tanstack/react-router';
import { Alert, Button, Card, Group, ScrollArea, Skeleton, Stack, Table, TextInput, Title } from '@mantine/core';
import { IconInfoCircle, IconCheck, IconRefresh } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { httpRequest } from '@/shared/api/client';

export const Route = createFileRoute('/_app/admin/structure-labels')({ component: StructureLabelsPage });

interface StructureLabel { key: string; defaultLabel: string; customLabel?: string | null; }

function StructureLabelsPage() {
  const qc = useQueryClient();
  const labelsQ = useQuery({
    queryKey: ['structure-labels'],
    queryFn: () => httpRequest<StructureLabel[]>('/api/structure-labels', { endpointType: 'plain' }),
  });

  const [drafts, setDrafts] = useState<Record<string, string>>({});

  useEffect(() => {
    const next: Record<string, string> = {};
    for (const l of labelsQ.data ?? []) next[l.key] = l.customLabel ?? '';
    setDrafts(next);
  }, [labelsQ.data]);

  const save = useMutation({
    mutationFn: (args: { key: string; value: string }) =>
      httpRequest(`/api/structure-labels/${encodeURIComponent(args.key)}`, {
        method: 'PATCH',
        body: { value: args.value },
        endpointType: 'plain',
      }),
    onSuccess: () => {
      notifications.show({ color: 'green', message: 'Etichetta salvata' });
      qc.invalidateQueries({ queryKey: ['structure-labels'] });
    },
    onError: () => notifications.show({ color: 'red', message: 'Salvataggio fallito' }),
  });

  return (
    <Stack maw={720}>
      <Title order={1}>Etichette di struttura organizzativa</Title>
      <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />}>
        Personalizza le etichette mostrate nella UI per le entita organizzative del tuo tenant (es. chiama "Reparto" invece di "Area"). Lascia vuoto per usare il default.
      </Alert>
      <Card withBorder padding={0} radius="md">
        {labelsQ.isLoading ? <Skeleton h={200} m="md" /> : (
          <ScrollArea type="auto">
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 120 }}>Key</Table.Th>
                <Table.Th>Default</Table.Th>
                <Table.Th>Etichetta custom (tenant)</Table.Th>
                <Table.Th style={{ width: 110 }}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(labelsQ.data ?? []).map((l) => (
                <Table.Tr key={l.key}>
                  <Table.Td><code>{l.key}</code></Table.Td>
                  <Table.Td>{l.defaultLabel}</Table.Td>
                  <Table.Td>
                    <TextInput
                      size="xs"
                      placeholder={l.defaultLabel}
                      value={drafts[l.key] ?? ''}
                      onChange={(e) => setDrafts((d) => ({ ...d, [l.key]: e.currentTarget.value }))}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      <Button size="xs" leftSection={<IconCheck size={12} />} loading={save.isPending} onClick={() => save.mutate({ key: l.key, value: drafts[l.key] ?? '' })}>
                        Salva
                      </Button>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          </ScrollArea>
        )}
      </Card>
      <Group>
        <Button variant="subtle" leftSection={<IconRefresh size={14} />} onClick={() => qc.invalidateQueries({ queryKey: ['structure-labels'] })}>
          Aggiorna
        </Button>
      </Group>
    </Stack>
  );
}
